/**
 * Where the camera looks, by phase (05 §3): pulled far back over the aircraft on the recon
 * pass, closing in as the parafoil descends, then following the ranger with a lean toward
 * his aim and a bias north - the threat is usually ahead.
 */

import type { PixelCamera } from '../render/camera/PixelCamera';
import { drawnAltitude } from '../render/world/altitude';
import type { World } from '../sim/types';

const GROUND_PPM_SCALE = 1;
const FLYOVER_PPM_SCALE = 0.34;
const CANOPY_PPM_SCALE = 0.5;
const DROP_ALTITUDE = 60;

export class MissionCamera {
  private started = false;

  constructor(private readonly camera: PixelCamera, private readonly groundPpm: number) {}

  update(world: World, alpha: number, dt: number, shakeX: number, shakeZ: number): void {
    const ins = world.insertion;
    const r = world.ranger;
    let tx: number;
    let tz: number;
    let scale: number;
    let stiffness: number;

    if (ins.phase === 'ground') {
      const rx = r.prevX + (r.x - r.prevX) * alpha;
      const rz = r.prevZ + (r.z - r.prevZ) * alpha;
      tx = rx + Math.sin(r.aim) * 3.2;
      tz = rz + Math.cos(r.aim) * 3.2 - 2.2;
      scale = GROUND_PPM_SCALE;
      stiffness = 6;
    } else {
      const x = ins.prevX + (ins.x - ins.prevX) * alpha;
      const z = ins.prevZ + (ins.z - ins.prevZ) * alpha;
      tx = x;
      // On the recon pass, look a little ahead of the aircraft: that is where pods will land.
      // and frame the machine and its shadow together: height shows as so much ground to the north.
      tz = (ins.phase === 'flyover' ? z + 6 : z) - drawnAltitude(ins.altitude) / 2;
      const descended = 1 - Math.min(1, Math.max(0, ins.altitude / DROP_ALTITUDE));
      scale = ins.phase === 'flyover' ? FLYOVER_PPM_SCALE : CANOPY_PPM_SCALE + (GROUND_PPM_SCALE - CANOPY_PPM_SCALE) * descended * descended;
      stiffness = 10;
    }

    const focus = this.camera.focus;
    const wanted = this.groundPpm * scale;
    if (!this.started) {
      // First frame: be where we should be, at the right zoom - no swoop in from nowhere.
      focus.set(tx, 0, tz);
      this.camera.pixelsPerMetre = wanted;
      this.started = true;
    } else {
      const k = 1 - Math.exp(-dt * stiffness);
      focus.x += (tx - focus.x) * k;
      focus.z += (tz - focus.z) * k;
    }
    // Zoom eases too, so touchdown does not snap the view.
    const ppm = this.camera.pixelsPerMetre;
    this.camera.pixelsPerMetre = Math.abs(wanted - ppm) < 0.02 ? wanted : ppm + (wanted - ppm) * (1 - Math.exp(-dt * 3));

    const baseX = focus.x;
    const baseZ = focus.z;
    focus.x += shakeX;
    focus.z += shakeZ;
    this.camera.update();
    focus.x = baseX;
    focus.z = baseZ;
  }
}
